// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewRejectScopePrompt.test.tsx
import { Button, Flex, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/** How far a rejection reaches, as she clicked it (warhol spec surbhi.kapoor/0100). */
export type RejectionReach = "once" | "rule";

export type RejectionChoice = {
  reach: RejectionReach;
  /** Her reason, trimmed; empty when she gave none for a one-time call. */
  reason: string;
};

/** Same bound as the ledger's `judgmentReason` and the schema. */
export const REJECTION_REASON_MAX_CHARS = 1000;

export type AskV2ReviewRejectScopePromptProps = {
  commentId: string;
  /** Prefix of every id and test id here; a second prompt for the same finding on screen needs its own. */
  testIdPrefix?: string;
  disabled?: boolean;
  onConfirm: (choice: RejectionChoice) => void;
  onCancel: () => void;
};

/**
 * Why the finding is wrong, and how far that reaches.
 *
 * The reach is the button she clicks, never a word in her reason: "Just this
 * review" clears the finding here and teaches nothing; "Make this an
 * enterprise rule" also sends her reason to the chat, where Puntt shows the
 * rule it understood and she corrects or confirms it. A rule needs a reason,
 * because the rule is read from it; a one-time call does not.
 */
export function AskV2ReviewRejectScopePrompt({
  commentId,
  testIdPrefix = "ask-v2-reject",
  disabled = false,
  onConfirm,
  onCancel,
}: AskV2ReviewRejectScopePromptProps) {
  const { t } = useTranslation("ui");
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  const titleId = `${testIdPrefix}-scope-title-${commentId}`;
  const reasonId = `${testIdPrefix}-reason-${commentId}`;

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      data-testid={`${testIdPrefix}-scope-prompt-${commentId}`}
      className="mt-3 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-3"
    >
      <Text
        as="p"
        size="1"
        weight="medium"
        id={titleId}
        data-testid={`${testIdPrefix}-scope-heading-${commentId}`}
      >
        {t("assetViewer.rejectWhyHeading")}
      </Text>

      <label
        htmlFor={reasonId}
        className="mt-2 block text-xs text-puntt-neutral-gray-11"
      >
        {t("assetViewer.rejectReasonLabel")}
      </label>
      <textarea
        id={reasonId}
        data-testid={`${testIdPrefix}-reason-${commentId}`}
        className="ph-no-capture mt-1 w-full rounded border border-puntt-neutral-gray-6 bg-base-white p-2 text-sm"
        rows={3}
        maxLength={REJECTION_REASON_MAX_CHARS}
        value={reason}
        disabled={disabled}
        onChange={(event) => setReason(event.target.value)}
      />

      <Flex gap="2" mt="3" wrap="wrap">
        <Button
          type="button"
          size="1"
          variant="outline"
          color="gray"
          disabled={disabled}
          data-testid={`${testIdPrefix}-once-${commentId}`}
          onClick={() => onConfirm({ reach: "once", reason: trimmed })}
        >
          {t("assetViewer.rejectOnce")}
        </Button>
        <Button
          type="button"
          size="1"
          variant="solid"
          color="gray"
          disabled={disabled || !trimmed}
          data-testid={`${testIdPrefix}-rule-${commentId}`}
          onClick={() => onConfirm({ reach: "rule", reason: trimmed })}
        >
          {t("assetViewer.rejectRule")}
        </Button>
        <Button
          type="button"
          size="1"
          variant="ghost"
          color="gray"
          disabled={disabled}
          data-testid={`${testIdPrefix}-cancel-${commentId}`}
          onClick={() => {
            setReason("");
            onCancel();
          }}
        >
          {t("assetViewer.rejectCancel")}
        </Button>
      </Flex>
      <Text
        as="p"
        size="1"
        mt="2"
        data-testid={`${testIdPrefix}-rule-hint-${commentId}`}
        className="text-puntt-neutral-gray-9"
      >
        {t("assetViewer.rejectRuleHint")}
      </Text>
    </div>
  );
}
